import { Form, Input as AntdInput } from 'antd';
import React, { FC, memo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from 'app/config/routerConfig/types';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { showNotification } from 'app/providers/NotificationsProvider';
import { useResetPasswordMutation, useSetNewPasswordMutation, useVerifyResetPasswordMutation } from 'entities/User';
import { Button } from 'shared/ui/Button';
import { Input } from 'shared/ui/Input';
import { isErrorApiResponse } from 'shared/utils/helpers/isErrorApiResponse';
import { useCountDown } from 'shared/utils/hooks/useCountDown';

type ForgotPasswordStep = 'email' | 'code' | 'password';

interface ForgotPasswordStepsProps {
  onSuccess?: () => void;
}

export const ForgotPasswordSteps: FC<ForgotPasswordStepsProps> = memo((props) => {
  const { onSuccess } = props;
  const { t } = useAppTranslation(['login-page', 'common']);
  const navigate = useNavigate();

  const [step, setStep] = useState<ForgotPasswordStep>('email');
  const [email, setEmail] = useState('');
  const [resetSessionId, setResetSessionId] = useState('');

  const { isCountDown, secondsLeft, runCountDown } = useCountDown();

  const [resetPassword, { isLoading: isResetLoading }] = useResetPasswordMutation();
  const [verifyResetPassword, { isLoading: isVerifyLoading }] = useVerifyResetPasswordMutation();
  const [setNewPassword, { isLoading: isSetPasswordLoading }] = useSetNewPasswordMutation();

  const [emailForm] = Form.useForm();
  const [codeForm] = Form.useForm();
  const [passwordForm] = Form.useForm();

  const handleSendCode = useCallback(
    async ({ email: submittedEmail }: { email: string }) => {
      try {
        const { resetSessionId: sessionId } = await resetPassword(submittedEmail).unwrap();
        setEmail(submittedEmail);
        setResetSessionId(sessionId);
        runCountDown(60);
        setStep('code');
        showNotification('info', t('Success', { ns: 'common' }), t('Instructions was sent on your email', { ns: 'common' }));
      } catch (error: CustomAny) {
        if (isErrorApiResponse(error)) {
          showNotification('error', t('Error', { ns: 'common' }), t(error.data.message, { ns: 'common' }));
        } else {
          console.log(error);
        }
      }
    },
    [resetPassword, runCountDown, t],
  );

  const handleResendCode = useCallback(async () => {
    if (!email || isCountDown) {
      return;
    }

    try {
      const { resetSessionId: sessionId } = await resetPassword(email).unwrap();
      setResetSessionId(sessionId);
      runCountDown(60);
      showNotification('info', t('Success', { ns: 'common' }), t('Instructions was sent on your email', { ns: 'common' }));
    } catch (error: CustomAny) {
      if (isErrorApiResponse(error)) {
        showNotification('error', t('Error', { ns: 'common' }), t(error.data.message, { ns: 'common' }));
      } else {
        console.log(error);
      }
    }
  }, [email, isCountDown, resetPassword, runCountDown, t]);

  const handleVerifyCode = useCallback(
    async ({ code }: { code: string }) => {
      try {
        await verifyResetPassword({ resetSessionId, code }).unwrap();
        setStep('password');
      } catch (error: CustomAny) {
        if (isErrorApiResponse(error)) {
          showNotification('error', t('Error', { ns: 'common' }), t(error.data.message, { ns: 'common' }));
        } else {
          console.log(error);
        }
      }
    },
    [resetSessionId, t, verifyResetPassword],
  );

  const handleSetPassword = useCallback(
    async ({ newPassword }: { newPassword: string }) => {
      try {
        await setNewPassword({ newPassword, resetSessionId }).unwrap();
        showNotification('info', t('Success', { ns: 'common' }), t('Password reset has been successful'));
        onSuccess?.();
        navigate(AppRoutes.HOME);
      } catch (error: CustomAny) {
        if (isErrorApiResponse(error)) {
          showNotification('error', t('Error', { ns: 'common' }), t(error.data.message, { ns: 'common' }));
        } else {
          showNotification('error', t('Error', { ns: 'common' }), t('Error when resetting password'));
        }
      }
    },
    [navigate, onSuccess, resetSessionId, setNewPassword, t],
  );

  if (step === 'code') {
    return (
      <Form name="verifyResetPasswordCode" form={codeForm} onFinish={handleVerifyCode}>
        <div className="text-primary mb-4">{t('Enter the code sent to your email')}</div>
        <Form.Item name="code" rules={[{ required: true, message: t('Please enter the code!') }]}>
          <Input placeholder={t('Verification code')} bordered maxLength={6} />
        </Form.Item>
        <Button type="submit" isLoading={isVerifyLoading}>
          {t('Verify code')}
        </Button>
        <Button containerClassName="mt-3" theme="secondary" isLoading={isResetLoading} isDisabled={isCountDown} onClick={handleResendCode}>
          {!isCountDown ? t('Resend code') : t('Resend instructions in {{secondsLeft}} sec', { secondsLeft })}
        </Button>
      </Form>
    );
  }

  if (step === 'password') {
    return (
      <Form name="setNewPassword" form={passwordForm} onFinish={handleSetPassword}>
        <Form.Item
          name="newPassword"
          rules={[
            { required: true, message: t('Please enter your new password!') },
            { min: 8, message: t('Password must be at least 8 characters!') },
          ]}
        >
          <AntdInput.Password className="h-14" placeholder={t('New password')} size="large" />
        </Form.Item>
        <Form.Item
          name="repeatPassword"
          rules={[
            { required: true, message: t('Please repeat password!') },
            ({ getFieldValue }) => ({
              async validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  await Promise.resolve();
                  return;
                }
                return await Promise.reject(new Error(t('Password must be the same')));
              },
            }),
          ]}
        >
          <AntdInput.Password placeholder={t('Repeat password')} size="large" />
        </Form.Item>
        <Button type="submit" isLoading={isSetPasswordLoading}>
          {t('Set new password')}
        </Button>
      </Form>
    );
  }

  return (
    <Form name="forgotPassword" form={emailForm} onFinish={handleSendCode}>
      <Form.Item
        name="email"
        rules={[
          { required: true, message: t('Please enter your email!') },
          { type: 'email', message: t('Please enter correct email!') },
        ]}
      >
        <Input placeholder={t('Your email')} bordered />
      </Form.Item>
      <Button type="submit" isLoading={isResetLoading}>
        {t('Send instructions')}
      </Button>
    </Form>
  );
});
